import { Link } from "react-router-dom"

import Badge from "~/v2/components/ui/badge"
import Button from "~/v2/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/v2/components/ui/dialog"
import { getVaasStatus } from "~/v2/lib/vaas-mapper"
import type { VaasCourse, VaasVersionSummary } from "~/v2/types/vaas"

const statusVariantMap = {
  archived: "warning",
  draft: "muted",
  published: "success",
  unpublished: "muted",
} as const satisfies Record<
  ReturnType<typeof getVaasStatus>,
  "muted" | "success" | "warning"
>

type HistoryVersion = VaasVersionSummary & Partial<VaasCourse>

const formatUpdatedLabel = (version: HistoryVersion) =>
  version?.updated?.format?.("M/D/YYYY") || "Unknown"

const sortVersions = (versions: HistoryVersion[]) =>
  [...versions].sort((left, right) => {
    const leftUpdated = left?.updated?.valueOf?.() || 0
    const rightUpdated = right?.updated?.valueOf?.() || 0

    if (leftUpdated !== rightUpdated) {
      return rightUpdated - leftUpdated
    }

    return String(right?.version || "").localeCompare(
      String(left?.version || ""),
      undefined,
      {
        numeric: true,
      },
    )
  })

const buildVersionList = (course?: VaasCourse | null) => {
  const versions = [...(course?.versions || []), course].filter(
    Boolean,
  ) as HistoryVersion[]
  const deduped = versions.filter(
    (version, index, list) =>
      list.findIndex((item) => item.id === version.id) === index,
  )

  return sortVersions(deduped)
}

const VaasHistoryDialog = ({
  course,
  onOpenChange,
  open,
}: {
  course?: VaasCourse | null
  onOpenChange: (open: boolean) => void
  open: boolean
}) => {
  const versions = buildVersionList(course)

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-2xl">
        <DialogClose />
        <DialogHeader>
          <DialogTitle>VAAS Version History</DialogTitle>
          <DialogDescription>
            Open any saved VAAS course version. Current version is highlighted.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
          {versions.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
              No versions available.
            </div>
          ) : (
            versions.map((version) => {
              const isCurrent = version.id === course?.id
              const status = getVaasStatus(version as VaasCourse)

              return (
                <div
                  className="flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-4 md:flex-row md:items-center md:justify-between"
                  key={version.id}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={statusVariantMap[status]}>{status}</Badge>
                      <Badge>Version {version.version || "Unknown"}</Badge>
                      {isCurrent && <Badge variant="success">Current</Badge>}
                    </div>
                    <div className="mt-2 text-sm font-medium text-foreground">
                      {version.name || course?.name || `VAAS ${version.id}`}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      Updated {formatUpdatedLabel(version)} · Course ID{" "}
                      {version.id}
                    </div>
                  </div>

                  <Link to={`/vaas/${version.id}`}>
                    <Button
                      onClick={() => onOpenChange(false)}
                      variant={isCurrent ? "ghost" : "outline"}
                    >
                      {isCurrent ? "Current version" : "Open version"}
                    </Button>
                  </Link>
                </div>
              )
            })
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default VaasHistoryDialog
